// SyncedBlock — BlockNote 커스텀 블록. 여러 페이지에서 같은 콘텐츠를 공유·동시 수정.
//
// - props.syncedId: workspace.synced_blocks.id (uuid). 콘텐츠 본문은 Yjs 문서가 아니라
//   DB(content jsonb = { text })에 저장 — 같은 syncedId 인스턴스가 페이지를 넘어 공유.
// - MVP 편집 UI 는 여러 줄 textarea (리치텍스트 아님).
// - 실시간: Supabase realtime postgres_changes(UPDATE, id=eq.<syncedId>) 구독 →
//   다른 페이지 인스턴스에 즉시 반영. 편집(포커스) 중엔 원격 반영을 보류해 타이핑 충돌 방지.
// - "블록 링크 복사" 로 syncedId 복사 → 다른 페이지에서 /동기화 블록 삽입 후 붙여넣어 연결.
// - graceful fallback: 테이블 미존재(마이그레이션 0024 미적용) 안내 / 비로그인(공개 페이지)
//   은 RLS 로 조회 불가 → "로그인 필요" 안내 (useFavorites.js 방어 패턴).
//
// ⚠️ createReactBlockSpec 은 생성 함수 반환 — 끝의 () 호출 필수(2026-06-18 사고, LinkedDatabaseView.jsx).

import { useCallback, useEffect, useRef, useState } from 'react';
import { createReactBlockSpec } from '@blocknote/react';
import { supabase } from '../../../supabaseClient';
import { useAuth } from '../../../contexts/AuthContext';
import './custom-blocks.css';

const SAVE_DEBOUNCE_MS = 600;

function genUuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // 구형 브라우저 폴백
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// 테이블 미존재(42P01) 판별 — PostgREST 는 스키마 캐시 미존재 시 PGRST205 를 주기도 한다.
function isMissingTable(error) {
  if (!error) return false;
  const code = String(error.code || '');
  const msg = String(error.message || '');
  return code === '42P01' || code === 'PGRST205' || /synced_blocks/.test(msg);
}

function SyncedBlockView({ block, editor }) {
  const editable = editor.isEditable;
  const { user, membership } = useAuth();
  const syncedId = block.props.syncedId || '';

  // status: idle(연결 전) | loading | ready | missing-table | denied | notfound | error
  const [status, setStatus] = useState(syncedId ? 'loading' : 'idle');
  const [text, setText] = useState('');
  const [connectDraft, setConnectDraft] = useState('');
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const focusedRef = useRef(false);
  const saveTimer = useRef(null);
  const copyTimer = useRef(null);

  // ── 로드 ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!syncedId) { setStatus('idle'); return undefined; }
    let active = true;
    setStatus('loading');
    (async () => {
      try {
        const { data, error } = await supabase
          .schema('workspace').from('synced_blocks')
          .select('content').eq('id', syncedId).maybeSingle();
        if (!active) return;
        if (error) {
          setStatus(isMissingTable(error) ? 'missing-table' : 'error');
        } else if (!data) {
          // RLS 로 행이 안 보임 — 비로그인(공개 페이지)과 삭제/권한 없음을 구분.
          setStatus(user ? 'notfound' : 'denied');
        } else {
          setText((data.content && typeof data.content.text === 'string') ? data.content.text : '');
          setStatus('ready');
        }
      } catch {
        if (active) setStatus('error');
      }
    })();
    return () => { active = false; };
  }, [syncedId, user]);

  // ── realtime 구독 — 다른 페이지 인스턴스의 수정 실시간 반영 ────────────
  useEffect(() => {
    if (!syncedId) return undefined;
    let ch;
    try {
      ch = supabase
        .channel(`rt-synced-${syncedId}-${Math.random().toString(36).slice(2, 8)}`)
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'workspace', table: 'synced_blocks', filter: `id=eq.${syncedId}` },
          (payload) => {
            const t = payload && payload.new && payload.new.content && payload.new.content.text;
            // 내가 타이핑 중이면 원격 반영 보류(blur 시 내 최신값이 저장돼 있음).
            if (typeof t === 'string' && !focusedRef.current) setText(t);
          },
        )
        .subscribe();
    } catch {
      ch = null; // realtime 실패해도 편집·저장은 동작(부가 기능).
    }
    return () => { if (ch) supabase.removeChannel(ch); };
  }, [syncedId]);

  // 타이머 정리
  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  // ── 저장(디바운스) ──────────────────────────────────────────────────────
  const persist = useCallback(async (next) => {
    try {
      await supabase
        .schema('workspace').from('synced_blocks')
        .update({ content: { text: next }, updated_at: new Date().toISOString() })
        .eq('id', syncedId);
    } catch { /* 저장 실패 — 다음 입력/blur 에서 재시도됨 */ }
  }, [syncedId]);

  const onChangeText = (next) => {
    setText(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(next), SAVE_DEBOUNCE_MS);
  };

  const onBlurText = () => {
    focusedRef.current = false;
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    persist(text);
  };

  // ── 새로 만들기 / 기존 ID 연결 ──────────────────────────────────────────
  const createNew = async () => {
    if (creating) return;
    setCreating(true);
    const id = genUuid();
    try {
      const { error } = await supabase
        .schema('workspace').from('synced_blocks')
        .insert({
          id,
          org_id: membership?.org_id || null,
          content: { text: '' },
          created_by: user?.id || null,
        });
      if (error) {
        setStatus(isMissingTable(error) ? 'missing-table' : 'error');
      } else {
        editor.updateBlock(block, { props: { syncedId: id } });
      }
    } catch {
      setStatus('error');
    }
    setCreating(false);
  };

  const connectExisting = () => {
    const id = connectDraft.trim();
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) return;
    editor.updateBlock(block, { props: { syncedId: id } });
  };

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(syncedId);
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1500);
    } catch { /* 클립보드 미지원 — 무시 */ }
  };

  // ── 렌더 ────────────────────────────────────────────────────────────────
  let body;
  if (!syncedId) {
    body = editable ? (
      <div className="bn-synced-setup">
        <button type="button" className="bn-synced-new" onClick={createNew} disabled={creating}>
          {creating ? '만드는 중…' : '새 동기화 블록 만들기'}
        </button>
        <div className="bn-synced-connect">
          <input
            className="bn-synced-connect-input"
            value={connectDraft}
            placeholder="기존 블록 ID 붙여넣기 (uuid)"
            spellCheck={false}
            onChange={(e) => setConnectDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); connectExisting(); } }}
          />
          <button type="button" className="bn-synced-connect-btn" onClick={connectExisting}>연결</button>
        </div>
      </div>
    ) : (
      <div className="bn-synced-note">연결되지 않은 동기화 블록입니다.</div>
    );
  } else if (status === 'loading') {
    body = <div className="bn-synced-note">동기화 블록을 불러오는 중…</div>;
  } else if (status === 'missing-table') {
    body = (
      <div className="bn-synced-note is-error">
        동기화 블록을 사용하려면 마이그레이션 0024(workspace.synced_blocks) 적용이 필요합니다.
      </div>
    );
  } else if (status === 'denied') {
    body = <div className="bn-synced-note">이 콘텐츠는 로그인해야 볼 수 있습니다.</div>;
  } else if (status === 'notfound') {
    body = <div className="bn-synced-note is-error">삭제되었거나 접근 권한이 없는 동기화 블록입니다.</div>;
  } else if (status === 'error') {
    body = <div className="bn-synced-note is-error">동기화 블록을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.</div>;
  } else if (editable) {
    body = (
      <textarea
        className="bn-synced-textarea"
        value={text}
        placeholder="동기화될 내용을 입력하세요"
        onChange={(e) => onChangeText(e.target.value)}
        onFocus={() => { focusedRef.current = true; }}
        onBlur={onBlurText}
      />
    );
  } else {
    body = <div className="bn-synced-text">{text || '빈 동기화 블록'}</div>;
  }

  return (
    <div className="bn-synced" contentEditable={false}>
      <div className="bn-synced-head">
        <span className="bn-synced-label">🔁 동기화 블록</span>
        {syncedId && status === 'ready' && editable ? (
          <button type="button" className="bn-synced-copy" onClick={copyId}>
            {copied ? '복사됨 ✓' : '블록 링크 복사'}
          </button>
        ) : null}
      </div>
      {body}
    </div>
  );
}

export const syncedBlockSpec = createReactBlockSpec(
  {
    type: 'syncedBlock',
    propSchema: {
      syncedId: { default: '' },
    },
    content: 'none',
  },
  {
    render: ({ block, editor }) => <SyncedBlockView block={block} editor={editor} />,
  },
)();

export default syncedBlockSpec;
